/**
 * PLAYING A CALL, AND READING ONE, FROM ANY SCREEN THAT SHOWS IT.
 *
 * Anthony, 2026-10-01: "fix not having recordings and transcripts pulling
 * to shop desk for New York Investigations."
 *
 * Two things were wrong and neither showed an error anybody would read.
 *
 * THE RECORDING LINK WAS CALLRAIL'S API ADDRESS. The intake sweep stores
 * the call's `recording` field, which is `api.callrail.com/.../recording`
 * and answers only to the account's API key. Opened from a browser it is a
 * login wall, so "Listen to the recording" never played anything for any
 * shop. The playable address carries a short-lived key and is fetched by
 * the server at the moment somebody presses Play.
 *
 * THE TRANSCRIPT ROUTE NEVER ASKED WHO WAS CALLING. It checked the call
 * against shop(), and on a request shop() is always Supercanic. So a NYIA
 * login pressing "Read the call" was told the call was not on their board.
 *
 * Both buttons now send the login's own token, and the server checks the
 * call against that login's shop. One delegated listener, so a button
 * rendered by any screen works without that screen wiring it.
 */

import { api, db } from "../data/client.ts";
import { esc } from "./dom.ts";

/** The Play and Read buttons for one CallRail call. */
export function callButtonsHtml(callId: string | null | undefined, opts: { read?: boolean } = {}): string {
  const id = String(callId ?? "").trim();
  if (!/^CAL[a-zA-Z0-9]{8,}$/.test(id)) return "";
  /* One box each, so opening the words does not take the player away. */
  return `<div class="callwrap">
    <div class="callbtns">
      <button type="button" class="linkbtn" data-play-call="${esc(id)}">Play the recording</button>
      ${opts.read === false ? "" : `<button type="button" class="linkbtn" data-read-call="${esc(id)}">Read the call</button>`}
    </div>
    <div class="talk-tx" data-out="play"></div>
    <div class="talk-tx" data-out="read"></div>
  </div>`;
}

async function authed(path: string): Promise<{ ok: boolean; body: Record<string, string> }> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  const r = await fetch(api(path), {
    headers: { Authorization: `Bearer ${session?.access_token ?? ""}` },
  });
  const body = await r.json().catch(() => ({})) as Record<string, string>;
  return { ok: r.ok, body };
}

let wired = false;

/** Safe to call from every screen; it wires the document once. */
export function wireCallButtons(): void {
  if (wired) return;
  wired = true;
  document.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement | null)?.closest<HTMLButtonElement>(
      "button[data-play-call],button[data-read-call]");
    if (!b) return;
    e.preventDefault();
    const id = b.dataset["playCall"] ?? b.dataset["readCall"] ?? "";
    const play = !!b.dataset["playCall"];
    const out = b.closest(".callwrap")?.querySelector<HTMLElement>(
      `.talk-tx[data-out="${play ? "play" : "read"}"]`);
    if (!out || !id) return;

    // Pressing it again folds it away.
    if (out.innerHTML) {
      out.innerHTML = "";
      b.textContent = play ? "Play the recording" : "Read the call";
      return;
    }

    const label = b.textContent;
    b.disabled = true; b.textContent = play ? "Fetching the recording…" : "Fetching…";
    void (async () => {
      try {
        const r = await authed(play
          ? `/api/call-recording?id=${encodeURIComponent(id)}`
          : `/api/call-transcript?id=${encodeURIComponent(id)}`);
        if (play && r.body["url"]) {
          out.innerHTML = `<audio class="call-audio" controls autoplay preload="auto"
            src="${esc(r.body["url"])}"></audio>`;
        } else if (!play && r.body["transcript"]) {
          out.innerHTML = `<div class="talk-txt">${esc(r.body["transcript"])}</div>`;
        } else {
          out.innerHTML = `<p class="msg bad">${esc(r.body["error"]
            ?? (play ? "No recording on this call." : "No transcript on this call."))}</p>`;
        }
        b.textContent = play ? "Hide the recording" : "Hide the call";
      } catch (err) {
        out.innerHTML = `<p class="msg bad">${esc(err instanceof Error ? err.message : String(err))}</p>`;
        b.textContent = label;
      }
      b.disabled = false;
    })();
  });
}
